import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PlayArrow as PlayIcon, SmartToy as CopilotIcon } from '@mui/icons-material';
import { copilotApi, type CopilotConfirmation, type CopilotPlan, type CopilotPostMessageResponse } from '../../services/copilotApi';
import { useAuth } from '../../contexts/AuthContext';

const EmbeddedCopilot: React.FC = () => {
  const { effectiveUser: user } = useAuth();
  const capabilities = Array.isArray((user as any)?.capabilities) ? (user as any).capabilities : [];
  const canWriteAudit = capabilities.includes('audit:write');
  const [searchParams] = useSearchParams();
  const devicePreset = String(searchParams.get('device') || searchParams.get('device_id') || '').trim();
  const orgPreset = String(searchParams.get('org') || searchParams.get('org_id') || '').trim();
  const sitePreset = String(searchParams.get('site') || searchParams.get('site_id') || '').trim();
  const autostart = String(searchParams.get('autostart') || '').trim() === '1';
  const promptFromUrl = useMemo(() => {
    const raw = searchParams.get('prompt') || searchParams.get('message') || searchParams.get('q') || '';
    return String(raw || '').trim();
  }, [searchParams]);
  const safetyPatternContext = useMemo(() => {
    if (String(searchParams.get('source') || '').trim() !== 'safety_pattern_review') return null;
    return {
      org: orgPreset,
      site: String(searchParams.get('site') || '').trim(),
      from: String(searchParams.get('from') || '').trim(),
      to: String(searchParams.get('to') || '').trim(),
      hotspot: String(searchParams.get('hotspot') || '').trim(),
      dominantType: String(searchParams.get('dominantType') || '').trim(),
      evidenceConfidence: String(searchParams.get('evidenceConfidence') || '').trim(),
    };
  }, [orgPreset, searchParams]);

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [enabledError, setEnabledError] = useState<string | null>(null);

  const [deviceId, setDeviceId] = useState<string>(devicePreset);
  const [sessionId, setSessionId] = useState<string>('');

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const [plan, setPlan] = useState<CopilotPlan | null>(null);
  const [confirmations, setConfirmations] = useState<CopilotConfirmation[]>([]);
  const [results, setResults] = useState<any[] | null>(null);
  const [autoLaunchError, setAutoLaunchError] = useState<string | null>(null);
  const [autoLaunchStatus, setAutoLaunchStatus] = useState<'idle' | 'creating_session' | 'planning' | 'done'>('idle');
  const lastAutoLaunchKeyRef = useRef<string>('');

  const confByFingerprint = useMemo(() => {
    const m = new Map<string, CopilotConfirmation>();
    for (const c of confirmations || []) m.set(String(c.fingerprint || ''), c);
    return m;
  }, [confirmations]);

  useEffect(() => {
    const run = async () => {
      setEnabledError(null);
      try {
        const res = await copilotApi.getEnabled();
        setEnabled(Boolean(res.enabled));
      } catch (e: any) {
        setEnabled(null);
        setEnabledError(e?.response?.data?.error || e?.message || 'Failed to load copilot status');
      }
    };
    void run();
  }, []);

  useEffect(() => {
    const nextDevice = String(searchParams.get('device') || searchParams.get('device_id') || '').trim();
    if (nextDevice) setDeviceId(nextDevice);
  }, [searchParams]);

  useEffect(() => {
    if (!promptFromUrl) return;
    setMessage(promptFromUrl);
  }, [promptFromUrl]);

  const createSession = useCallback(async (scopeOverride?: { org_id?: string; site_id?: string; device_id?: string }) => {
    setCreateError(null);
    setCreating(true);
    try {
      const res = await copilotApi.createSession({
        scope: {
          org_id: scopeOverride?.org_id || undefined,
          site_id: scopeOverride?.site_id || undefined,
          device_id: scopeOverride?.device_id || deviceId || undefined,
        },
        mode: 'embedded',
      });
      const nextSessionId = String(res.session?.id || '');
      setSessionId(nextSessionId);
      return nextSessionId;
    } catch (e: any) {
      setCreateError(e?.response?.data?.error || e?.message || 'Failed to create session');
      return '';
    } finally {
      setCreating(false);
    }
  }, [deviceId]);

  const sendMessage = useCallback(async (sessionIdOverride?: string, messageOverride?: string) => {
    const activeSessionId = String(sessionIdOverride || sessionId || '').trim();
    if (!activeSessionId) {
      setSendError('Create a session first.');
      return;
    }
    const msg = String(messageOverride || message || '').trim();
    if (!msg) return;
    setSendError(null);
    setSending(true);
    setPlan(null);
    setConfirmations([]);
    setResults(null);
    try {
      const res: CopilotPostMessageResponse = await copilotApi.postMessage(activeSessionId, msg);
      setPlan(res.plan);
      setConfirmations(res.confirmations || []);
      return true;
    } catch (e: any) {
      setSendError(e?.response?.data?.error || e?.message || 'Failed to get plan');
      return false;
    } finally {
      setSending(false);
    }
  }, [message, sessionId]);

  useEffect(() => {
    if (!autostart || !promptFromUrl || !canWriteAudit || enabled !== true) return;
    const autoLaunchKey = JSON.stringify({
      prompt: promptFromUrl,
      device: devicePreset || deviceId,
      org: orgPreset,
      site: sitePreset,
      source: safetyPatternContext ? 'safety_pattern_review' : '',
    });
    if (lastAutoLaunchKeyRef.current === autoLaunchKey) return;
    lastAutoLaunchKeyRef.current = autoLaunchKey;
    setAutoLaunchError(null);

    const run = async () => {
      setAutoLaunchStatus('creating_session');
      const nextSessionId = await createSession({
        org_id: orgPreset || undefined,
        site_id: sitePreset || undefined,
        device_id: devicePreset || deviceId || undefined,
      });
      if (!nextSessionId) {
        setAutoLaunchStatus('idle');
        setAutoLaunchError('Could not auto-start Copilot from Risk review.');
        return;
      }
      setAutoLaunchStatus('planning');
      const ok = await sendMessage(nextSessionId, promptFromUrl);
      if (!ok) {
        setAutoLaunchStatus('idle');
        setAutoLaunchError('Copilot session started, but the seeded Risk review prompt could not be planned automatically.');
        return;
      }
      setAutoLaunchStatus('done');
    };

    void run();
  }, [
    autostart,
    canWriteAudit,
    createSession,
    deviceId,
    devicePreset,
    enabled,
    orgPreset,
    promptFromUrl,
    safetyPatternContext,
    sendMessage,
    sitePreset,
  ]);

  const approveAndRun = async (confirmationId: string) => {
    if (!sessionId) return;
    setSendError(null);
    setSending(true);
    setResults(null);
    try {
      const res = await copilotApi.confirm(sessionId, { confirmation_id: confirmationId });
      setResults(res.results || []);
    } catch (e: any) {
      setSendError(e?.response?.data?.error || e?.message || 'Execution failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <CopilotIcon color="primary" />
        <Typography variant="h4">Embedded Copilot</Typography>
        {enabled === true ? <Chip size="small" color="success" label="enabled" /> : enabled === false ? <Chip size="small" label="disabled" /> : null}
      </Box>

      {enabledError && <Alert severity="warning" sx={{ mb: 2 }}>{enabledError}</Alert>}
      {enabled === false && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Embedded Copilot is disabled. Set <strong>EMBEDDED_COPILOT_ENABLED=true</strong> on the server to enable.
        </Alert>
      )}
      {!canWriteAudit && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You have read-only audit access. Copilot session creation and execution are disabled.
        </Alert>
      )}

      {safetyPatternContext ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Opened from Risk review
          </Typography>
          <Typography variant="body2" component="div" sx={{ lineHeight: 1.45 }}>
            {[
              safetyPatternContext.site ? `Site: ${safetyPatternContext.site}` : null,
              safetyPatternContext.from && safetyPatternContext.to
                ? `Window: ${safetyPatternContext.from} → ${safetyPatternContext.to}`
                : null,
              safetyPatternContext.hotspot ? `High-risk area: ${safetyPatternContext.hotspot}` : null,
              safetyPatternContext.dominantType ? `Main recurring risk: ${safetyPatternContext.dominantType}` : null,
              safetyPatternContext.evidenceConfidence ? `Evidence strength: ${safetyPatternContext.evidenceConfidence}` : null,
            ]
              .filter(Boolean)
              .join(' · ') || 'Context was passed in the URL; use Start session, then Plan, with the prefilled prompt below.'}
          </Typography>
        </Alert>
      ) : null}
      {autoLaunchStatus === 'creating_session' ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Starting a scoped Copilot session from Risk review…
        </Alert>
      ) : null}
      {autoLaunchStatus === 'planning' ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Sending the seeded Risk review prompt to Copilot…
        </Alert>
      ) : null}
      {autoLaunchStatus === 'done' ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Copilot session started with Risk review context.
        </Alert>
      ) : null}
      {autoLaunchError ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {autoLaunchError}
        </Alert>
      ) : null}

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              label="Device ID (optional)"
              size="small"
              value={deviceId}
              onChange={(e) => setDeviceId(String(e.target.value || ''))}
              helperText="Scope determines recommended actions and runbook constraints."
              sx={{ flexGrow: 1 }}
            />
            <Button variant="contained" onClick={() => void createSession()} disabled={!canWriteAudit || creating || enabled !== true}>
              {creating ? <CircularProgress size={16} color="inherit" /> : 'Start session'}
            </Button>
          </Stack>
          {createError && <Alert severity="error" sx={{ mt: 2 }}>{createError}</Alert>}
          {sessionId && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Session: <strong>{sessionId}</strong> {user?.username ? `- user: ${user.username}` : ''}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Ask for an action</Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              placeholder={'E.g. "reset failed evidence uploads" or mention a runbook id'}
              value={message}
              onChange={(e) => setMessage(String(e.target.value || ''))}
              disabled={!canWriteAudit || sending || enabled !== true}
            />
            <Button
              variant="contained"
              startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <PlayIcon />}
              onClick={() => void sendMessage()}
              disabled={!canWriteAudit || sending || enabled !== true}
            >
              Plan
            </Button>
          </Stack>

          {sendError && <Alert severity="error" sx={{ mt: 2 }}>{sendError}</Alert>}

          {plan && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Plan</Typography>
              {plan.guidance && <Alert severity="info" sx={{ mt: 1 }}>{plan.guidance}</Alert>}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                templates_version: {plan.template_version}
              </Typography>
              <Stack gap={1} sx={{ mt: 2 }}>
                {(plan.actions || []).map((a) => {
                  const fp = String(a.fingerprint || '');
                  const conf = confByFingerprint.get(fp);
                  const requires = Boolean(a.requires_confirmation);
                  const canRun = requires ? Boolean(conf?.confirmation_id) : false;
                  return (
                    <Box key={fp} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                      <Stack direction={{ xs: 'column', md: 'row' }} gap={1} alignItems={{ md: 'center' }} justifyContent="space-between">
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {a.label || a.runbook_id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            runbook_id: {a.runbook_id} • fingerprint: {fp.slice(0, 18)}…
                          </Typography>
                        </Box>
                        <Stack direction="row" gap={1} alignItems="center">
                          {requires ? <Chip size="small" color="warning" label="confirm required" /> : <Chip size="small" label="read-only" />}
                          <Button
                            size="small"
                            variant="contained"
                            disabled={!canWriteAudit || !canRun || sending}
                            onClick={() => void approveAndRun(String(conf?.confirmation_id || ''))}
                          >
                            Approve & Run
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          {results && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Results</Typography>
              <pre style={{ maxHeight: 420, overflow: 'auto', marginTop: 8 }}>
                {JSON.stringify(results, null, 2)}
              </pre>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default EmbeddedCopilot;
